import { t } from 'i18next';

import { AlertMessage } from '../../constants/alert-message';
import { ApiErrorResponse } from '../../types/api';
import { ApiEndpoints } from '../constants/api';
import { ApiGroupNames, EndpointQueryNames } from '../constants/api-group-names';
import { EndpointNames } from '../constants/endpoint-names';
import { transformErrorWithAlert } from '../helpers/transform-error-with-alert';
import { requestProcessing } from '../helpers/request-processing';
import { HttpStatus } from '../../constants/http-status';
import { Paths } from '../../routes/paths-enum';
import { Statuses } from '../../types/general';
import {
  ApplicationAcceptResponseType,
  ApplicationConfirmRequestType,
  ApplicationPayloadType,
  ApplicationResponseType,
  GetApplicationPayloadType,
  RequestAppCheckOtpType,
  RequestApplicationAcceptType,
  ResponseApplicationCheckOtpType,
  ResponseAppAcceptType,
  ApplicationPaymentToolResponseType,
  ApplicationStatusResponseType,
  GetApplicationPaymentToolPayloadType,
  GetApplicationStatusPayloadType,
  HistoryResponseType,
  RequestApplicationPaymentToolType,
  ResponseApplicationPaymentToolType,
  ApplicationAcceptPayloadType,
  ResendApplicationPayloadType,
  ResponsePartnersType,
  ApplicationConfirmResponseType,
  ResendApplicationConfirmType,
  RequestAppConfirmCheckOtpType,
} from '../../types/application';
import { setApplicationId, setResultInfo } from '../modules/app';
import { ResponseRegistrationType } from '../../types/registration';
import { divisionByHundred } from '../../utils/division-by-hundred';
import { ErrorCode } from '../../constants/error-code';
import { ResultType } from '../../constants/result';
import { history, store } from '../configure-store';
import { Widget } from '../../constants/widget';
import { LOCAL_STORAGE } from '../../constants/local-storage';
import { GtmEventEnum, sendGtmEvent } from '../../utils/send-gtm-event';
import AppsFlyer from '../../utils/appsflyer';
import { AppsflyerEventName } from '../../types/appsflyer';
import { showAlertAndRedirect } from '../../utils/show-alert-and-redirect';

import { apiSlice } from '.';

export const applicationApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getApplication: builder.query<ApplicationResponseType, GetApplicationPayloadType>({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'GET',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION,
      }),
      transformErrorResponse: (response: ApiErrorResponse) =>
        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        }),
      transformResponse: (response: ApplicationResponseType) => {
        const { applicationId, repaymentAmount, requestedAmount } = response;

        store.dispatch(setApplicationId(applicationId));

        return {
          ...response,
          requestedAmount: divisionByHundred(requestedAmount),
          repaymentAmount: divisionByHundred(repaymentAmount),
        };
      },
    }),

    postApplicationConfirm: builder.mutation<
      ApplicationConfirmResponseType,
      ApplicationConfirmRequestType
    >({
      query: ({ data, applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_CONFIRM}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION_CONFIRM,
        body: data,
      }),
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        const errorHandlers = {
          [ErrorCode.ERROR_REVIEW_APPLICATION]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.TEMPORARY_ERROR_OCCURRED }),
          [ErrorCode.TIMEOUT]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.TIMEOUT_EXCEEDED }),
          [ErrorCode.UNDEFINED_NEXT_STEP]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.CHECK_STATUS_SUPPORT }),
          [ErrorCode.OTP_SEND_DISABLED]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.ERROR_SMS_SEND }),
          [ErrorCode.OTP_SEND_DISABLED_SPAM]: () =>
            transformErrorWithAlert({
              response,
              message: AlertMessage.INFO_SMS_SEND,
              type: Statuses.WARNING,
            }),
          [ErrorCode.NEED_START_AGAIN]: () =>
            showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS),
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.ERROR_SMS_SEND,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
      transformResponse: (response: any): any => {
        const { applicationId, nextStep } = response;

        store.dispatch(setApplicationId(applicationId));
        requestProcessing({ nextStep });

        return {
          ...response,
        };
      },
    }),

    resendApplicationConfirmOtp: builder.mutation<
      ResponseRegistrationType,
      ResendApplicationConfirmType
    >({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_CONFIRM_RESEND_OTP}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION_CONFIRM,
      }),
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        const errorHandlers = {
          [ErrorCode.NEED_START_AGAIN]: () =>
            showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS),
          [ErrorCode.OTP_SEND_DISABLED]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.ERROR_SMS_SEND_BLOCK }),
          [ErrorCode.OTP_SEND_DISABLED_SPAM]: () =>
            transformErrorWithAlert({
              response,
              message: AlertMessage.INFO_SMS_SEND,
              type: Statuses.WARNING,
            }),
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.ERROR_SMS_SEND,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
    }),

    applicationConfirmCheckOtp: builder.mutation<
      ResponseApplicationCheckOtpType,
      RequestAppConfirmCheckOtpType
    >({
      query: ({ applicationId, otp }) => ({
        url: `${ApiEndpoints.APPLICATION_CONFIRM_CHECK_OTP}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION,
        body: {
          otp,
        },
      }),

      transformResponse: (response: any): any => {
        const { nextStep, applicationId } = response;

        AppsFlyer.sendEvent(AppsflyerEventName.app_completed, {
          app_id: applicationId,
        });
        sendGtmEvent(GtmEventEnum.app_completed);

        requestProcessing({ nextStep, applicationId });

        return {
          ...response,
        };
      },
      transformErrorResponse: (response: ApiErrorResponse, _, args) => {
        const { data: { code, message } = {}, status } = response;
        const { inputError, setInputError } = args;

        sendGtmEvent(GtmEventEnum.confirmLoanApplicationSMS_wrongSMSErrorShown);

        const errorHandlers = {
          [ErrorCode.NEED_START_AGAIN]: () =>
            showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS),
          [ErrorCode.WRONG_OTP]: () =>
            store.dispatch(
              setInputError({
                ...inputError,
                inputSms: AlertMessage.ERROR_WRONG_OTP,
              }),
            ),
          [ErrorCode.ATTEMPTS_EXHAUSTED]: () =>
            store.dispatch(
              setInputError({
                ...inputError,
                inputSms: AlertMessage.ERROR_CODE_EXCEEDED,
              }),
            ),
          [ErrorCode.OTP_EXPIRED]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.ERROR_CODE_EXPIRED }),
          [ErrorCode.TIMEOUT]: () => {
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessHanding'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
          [ErrorCode.UNDEFINED_NEXT_STEP]: () => {
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessHanding'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && message === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.FAILED_VERIFY_SMS,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({ response, message: AlertMessage.ERROR_OTHER });
      },
    }),

    getApplicationAccept: builder.query<
      ApplicationAcceptResponseType,
      ApplicationAcceptPayloadType
    >({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_ACCEPT}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'GET',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION,
      }),
      transformErrorResponse: (response: ApiErrorResponse) =>
        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        }),
      transformResponse: (response: ApplicationAcceptResponseType) => {
        const { approvedAmount, repaymentAmount, requestedAmount, ...rest } = response;

        return {
          ...rest,
          approvedAmount: divisionByHundred(approvedAmount),
          repaymentAmount: divisionByHundred(repaymentAmount),
          requestedAmount: divisionByHundred(requestedAmount),
        };
      },
    }),

    applicationAccept: builder.mutation<ResponseAppAcceptType, RequestApplicationAcceptType>({
      query: ({ applicationId, agreements, deviceId, deviceModel, analyticsTags }) => ({
        url: `${ApiEndpoints.APPLICATION_ACCEPT}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION_ACCEPT,
        name: EndpointNames.APPLICATION_ACCEPT,
        body: {
          agreements,
          deviceId,
          deviceModel,
          analyticsTags,
        },
      }),
      transformResponse: (response: ResponseAppAcceptType): ResponseAppAcceptType => {
        const { applicationId, nextStep } = response;

        requestProcessing({ nextStep, applicationId });

        return {
          ...response,
        };
      },
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        const errorHandlers = {
          [ErrorCode.OTP_SEND_DISABLED]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.ERROR_SMS_SEND }),
          [ErrorCode.OTP_SEND_DISABLED_SPAM]: () =>
            transformErrorWithAlert({
              response,
              message: AlertMessage.INFO_SMS_SEND,
              type: Statuses.WARNING,
            }),
          [ErrorCode.NEED_START_AGAIN]: () =>
            showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS),
          [ErrorCode.UNDEFINED_NEXT_STEP]: () => {
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessHanding'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
          [ErrorCode.TIMEOUT]: () => {
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessHanding'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.ERROR_SMS_SEND,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
    }),

    applicationDecline: builder.mutation<void, ApplicationPayloadType>({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_DECLINE}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION_DECLINE,
        name: EndpointNames.APPLICATION_DECLINE,
      }),
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.BAD_REQUEST,
          });
        }
        if (
          (status === HttpStatus.REGISTRATION_PROBLEM && code === ErrorCode.IN_PROGRESS) ||
          (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS)
        ) {
          sendGtmEvent(GtmEventEnum.inProgress_screenShown);
          store.dispatch(
            setResultInfo({
              status: ResultType.info,
              title: t('result.loanApplicationProcessInterruption'),
              description: t('result.operationInProgress'),
              buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
            }),
          );
          history.push(Paths.RESULT_PAGE);
        }
        if (status === HttpStatus.REGISTRATION_PROBLEM && code === ErrorCode.NEED_START_AGAIN) {
          return showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS);
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
    }),

    applicationCancel: builder.mutation<void, ApplicationPayloadType>({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_CANCEL}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION_CANCEL,
        name: EndpointNames.APPLICATION_CANCEL,
      }),
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.BAD_REQUEST,
          });
        }
        if (
          (status === HttpStatus.REGISTRATION_PROBLEM && code === ErrorCode.IN_PROGRESS) ||
          (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS)
        ) {
          sendGtmEvent(GtmEventEnum.inProgress_screenShown);
          store.dispatch(
            setResultInfo({
              status: ResultType.info,
              title: t('result.loanApplicationProcessInterruption'),
              description: t('result.operationInProgress'),
              buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
            }),
          );
          history.push(Paths.RESULT_PAGE);
        }
      },
    }),

    resendApplicationOtp: builder.mutation<ResponseRegistrationType, ResendApplicationPayloadType>({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_RESEND_OTP}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION,
      }),
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        const errorHandlers = {
          [ErrorCode.NEED_START_AGAIN]: () =>
            showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS),
          [ErrorCode.OTP_SEND_DISABLED]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.ERROR_SMS_SEND_BLOCK }),
          [ErrorCode.OTP_SEND_DISABLED_SPAM]: () =>
            transformErrorWithAlert({
              response,
              message: AlertMessage.INFO_SMS_SEND,
              type: Statuses.WARNING,
            }),
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.ERROR_SMS_SEND,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
    }),

    applicationCheckOtp: builder.mutation<ResponseApplicationCheckOtpType, RequestAppCheckOtpType>({
      query: ({ applicationId, otp }) => ({
        url: `${ApiEndpoints.APPLICATION_CHECK_OTP}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION,
        body: {
          otp,
        },
      }),
      transformResponse: (response: ApplicationStatusResponseType): any => {
        const { applicationId, nextStep } = response;

        requestProcessing({ nextStep, applicationId });

        return {
          ...response,
        };
      },
      transformErrorResponse: (response: ApiErrorResponse, _, args) => {
        const { data: { code } = {}, status } = response;
        const { inputError, setInputError } = args;

        sendGtmEvent(GtmEventEnum.loanApprovedSMS_wrongSMSErrorShown);

        const errorHandlers = {
          [ErrorCode.NEED_START_AGAIN]: () =>
            showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS),
          [ErrorCode.WRONG_OTP]: () =>
            store.dispatch(
              setInputError({
                ...inputError,
                inputSms: AlertMessage.ERROR_WRONG_OTP,
              }),
            ),
          [ErrorCode.ATTEMPTS_EXHAUSTED]: () =>
            store.dispatch(
              setInputError({
                ...inputError,
                inputSms: AlertMessage.ERROR_CODE_EXCEEDED,
              }),
            ),
          [ErrorCode.OTP_EXPIRED]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.ERROR_CODE_EXPIRED }),
          [ErrorCode.TIMEOUT]: () => {
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessHanding'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
          [ErrorCode.UNDEFINED_NEXT_STEP]: () => {
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessHanding'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.FAILED_VERIFY_SMS,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
    }),

    getHistory: builder.query<HistoryResponseType[], void>({
      query: () => ({
        url: ApiEndpoints.HISTORY,
        endpoint: EndpointQueryNames.HISTORY_OPERATOR,
        method: 'GET',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.HISTORY,
      }),
      transformResponse: (response: HistoryResponseType[]): any => {
        const isLoanInRes = response.find((item) => item?.widget === Widget.loan);
        const isAppInRes = response.find((item) => item?.widget === Widget.application);

        sessionStorage.setItem(LOCAL_STORAGE.isNewClient, isLoanInRes ? 'false' : 'true');
        sessionStorage.setItem(LOCAL_STORAGE.isNewApp, isAppInRes ? 'false' : 'true');

        return response;
      },
    }),

    getApplicationStatus: builder.query<
      ApplicationStatusResponseType,
      GetApplicationStatusPayloadType
    >({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_STATUS}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'GET',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION_STATUS,
      }),
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        const errorHandlers = {
          [ErrorCode.IN_PROGRESS]: () => {
            sendGtmEvent(GtmEventEnum.inProgress_screenShown);
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessInterruption'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
          [ErrorCode.UNDEFINED_NEXT_STEP]: () => {
            store.dispatch(
              setResultInfo({
                status: ResultType.info,
                title: t('result.loanApplicationProcessInterruption'),
                description: t('result.operationInProgress'),
                buttons: [{ type: 'redirect', path: Paths.HOME, text: t('result.buttonHome') }],
              }),
            );
            history.push(Paths.RESULT_PAGE);
          },
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.TEMPORARY_PROBLEMS,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        return transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
      transformResponse: (response: ApplicationStatusResponseType): any => {
        const { applicationId, nextStep } = response;

        store.dispatch(setApplicationId(applicationId));
        requestProcessing({ nextStep });

        return {
          ...response,
        };
      },
    }),

    getApplicationPaymentTool: builder.query<
      ApplicationPaymentToolResponseType,
      GetApplicationPaymentToolPayloadType
    >({
      query: ({ applicationId }) => ({
        url: `${ApiEndpoints.APPLICATION_PAYMENT_TOOL}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'GET',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION_PAYMENT_TOOL,
      }),
      transformErrorResponse: (response: ApiErrorResponse) => {
        const { data: { code } = {}, status } = response;

        const errorHandlers = {
          [ErrorCode.TIMEOUT]: () => showAlertAndRedirect(response, AlertMessage.TIMEOUT_EXCEEDED),
          [ErrorCode.UNDEFINED_NEXT_STEP]: () =>
            showAlertAndRedirect(response, AlertMessage.CHECK_STATUS_SUPPORT),
          [ErrorCode.INVALID_APPLICATION_ID]: () =>
            transformErrorWithAlert({
              response,
              message: AlertMessage.INCORRECT_DATA_SUPPORT,
            }),
          [ErrorCode.ABSENT_EMAIL]: () =>
            transformErrorWithAlert({
              response,
              message: AlertMessage.EMAIL_NOT_SET,
            }),
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (status === HttpStatus.TEMPORARY_PROBLEMS && code === ErrorCode.TEMPORARY_PROBLEMS) {
          return transformErrorWithAlert({
            response,
            message: AlertMessage.TEMPORARY_PROBLEMS,
          });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
    }),

    ApplicationPaymentTool: builder.mutation<
      ResponseApplicationPaymentToolType,
      RequestApplicationPaymentToolType
    >({
      query: ({ applicationId, account, analyticsTags, deviceDetails }) => ({
        url: `${ApiEndpoints.APPLICATION_PAYMENT_TOOL}/${applicationId}`,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'POST',
        apiGroupName: ApiGroupNames.APPLICATION,
        name: EndpointNames.APPLICATION_PAYMENT_TOOL,
        body: {
          account,
          analyticsTags,
          deviceDetails,
        },
      }),
      transformErrorResponse: (response: ApiErrorResponse, meta, args) => {
        const { data, status } = response;
        const { setInputError, inputError } = args;

        const errorHandlers = {
          [ErrorCode.TIMEOUT]: () => showAlertAndRedirect(response, AlertMessage.TIMEOUT_EXCEEDED),
          [ErrorCode.UNDEFINED_NEXT_STEP]: () =>
            showAlertAndRedirect(response, AlertMessage.CHECK_STATUS_SUPPORT),
          [ErrorCode.INVALID_APPLICATION_ID]: () =>
            transformErrorWithAlert({ response, message: AlertMessage.INCORRECT_DATA_SUPPORT }),
          [ErrorCode.ABSENT_EMAIL]: () =>
            showAlertAndRedirect(response, AlertMessage.EMAIL_NOT_SET, Paths.PROFILE),
          [ErrorCode.ERROR_ACCOUNT]: () =>
            store.dispatch(
              setInputError({
                ...inputError,
                inputAccountDestination: AlertMessage.INCORRECT_ACCOUNT_NUMBER,
              }),
            ),
          [ErrorCode.ACCOUNT_REGISTERED_ANOTHER_USER]: () =>
            store.dispatch(
              setInputError({
                ...inputError,
                inputAccountDestination: AlertMessage.MAKE_SURE_ACCOUNT,
              }),
            ),
          [ErrorCode.NEED_START_AGAIN]: () =>
            showAlertAndRedirect(response, AlertMessage.TEMPORARY_PROBLEMS),
        };

        if (status === HttpStatus.BAD_REQUEST) {
          return transformErrorWithAlert({ response, message: AlertMessage.BAD_REQUEST });
        }

        if (
          status === HttpStatus.TEMPORARY_PROBLEMS &&
          data?.code === ErrorCode.TEMPORARY_PROBLEMS
        ) {
          return transformErrorWithAlert({ response, message: AlertMessage.TEMPORARY_PROBLEMS });
        }

        if (status === HttpStatus.REGISTRATION_PROBLEM) {
          const errorHandler = errorHandlers[data?.code || ''];

          if (errorHandler) {
            return errorHandler();
          }
        }

        transformErrorWithAlert({
          response,
          message: AlertMessage.ERROR_OTHER,
        });
      },
      transformResponse: (response: any): any => {
        const { nextStep } = response;

        requestProcessing({ nextStep });

        return {
          ...response,
        };
      },
    }),

    getPartners: builder.query<ResponsePartnersType[], void>({
      query: () => ({
        url: ApiEndpoints.PARTNERS,
        endpoint: EndpointQueryNames.DEV_LOAN_ORIGINATION,
        method: 'GET',
        apiGroupName: ApiGroupNames.LOAN,
        name: EndpointNames.PARTNERS,
      }),
    }),
  }),
});

export const {
  useGetHistoryQuery,
  useLazyGetHistoryQuery,
  useLazyGetApplicationStatusQuery,
  useGetApplicationPaymentToolQuery,
  useApplicationPaymentToolMutation,
  useApplicationCheckOtpMutation,
  useApplicationDeclineMutation,
  useApplicationCancelMutation,
  useGetApplicationAcceptQuery,
  useApplicationAcceptMutation,
  useResendApplicationOtpMutation,
  useGetApplicationQuery,
  usePostApplicationConfirmMutation,
  useGetApplicationStatusQuery,
  useLazyGetPartnersQuery,
  useApplicationConfirmCheckOtpMutation,
  useResendApplicationConfirmOtpMutation,
} = applicationApiSlice;
